import { useEffect, useState } from "react";
import { RefreshCw, Trash2, Mail, Save, Image as ImageIcon, LogOut, LogIn, Type, ExternalLink, KeyRound } from "lucide-react";
import { IMAGE_SLOTS } from "../lib/imageSlots";
import { CONTENT_SLOTS } from "../lib/contentSlots";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { toast, Toaster } from "sonner";

/* =========================================================================
 * Login card
 * ========================================================================= */
function LoginCard() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Login failed");
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-4 pt-24 pb-24" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="tag-glass mb-6 mx-auto w-fit"><span className="dot" /> XMLOOPS · Admin</div>
        <h1 className="font-display text-white text-4xl md:text-5xl leading-tight tracking-tight text-center">
          Sign in to the <span className="italic-accent text-grad-crimson">control room</span>.
        </h1>
        <p className="text-white/60 text-center mt-4 text-sm">Enter your admin credentials to manage the site.</p>

        <form onSubmit={submit} className="mt-10 glass rounded-2xl p-8 space-y-5">
          <div>
            <label className="overline block mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              placeholder="admin@xmloops.com"
              className="w-full bg-transparent border border-white/12 focus:border-[#ff5865] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
              data-testid="login-email"
            />
          </div>
          <div>
            <label className="overline block mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              className="w-full bg-transparent border border-white/12 focus:border-[#ff5865] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
              data-testid="login-password"
            />
          </div>
          {error && <div className="text-[#ff5865] text-sm" data-testid="login-error">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full justify-center disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? "Signing in…" : "Sign in"}
            <LogIn className="w-4 h-4" />
          </button>
          <p className="text-[11px] text-white/40 flex items-center gap-2 pt-2">
            <KeyRound className="w-3 h-3" /> Admin credentials are stored in the server .env
          </p>
        </form>
      </div>
    </main>
  );
}

/* =========================================================================
 * Submissions
 * ========================================================================= */
function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/contact");
      setItems(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/contact/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-white/72 text-sm">{items.length} total</p>
        <button onClick={load} className="btn btn-glass !py-2 !px-4 !text-xs" data-testid="admin-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6 border border-white/10 rounded-2xl overflow-hidden">
          {items.length === 0 && !loading && <div className="p-10 text-white/50 text-sm">No submissions yet.</div>}
          <ul className="divide-y divide-white/5 max-h-[560px] overflow-y-auto">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  onClick={() => setSelected(it)}
                  className={`w-full text-left p-5 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors ${selected?.id === it.id ? "bg-white/5" : ""}`}
                  data-testid={`admin-item-${it.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-white truncate">{it.name} <span className="text-white/40 text-sm">· {it.company || "—"}</span></div>
                    <div className="text-white/50 text-xs mt-1 truncate">{it.email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-white/50 text-xs font-mono">{new Date(it.created_at).toLocaleDateString()}</div>
                    {it.service && <div className="text-[10px] mt-1 font-mono uppercase tracking-[0.2em] text-white/40">{it.service}</div>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 md:col-span-6 border border-white/10 rounded-2xl p-8 min-h-[360px]">
          {selected ? (
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-white/50 text-xs font-mono uppercase tracking-[0.25em]">{new Date(selected.created_at).toLocaleString()}</div>
                  <h2 className="font-display text-white text-3xl mt-2">{selected.name}</h2>
                  <div className="text-white/72 mt-1">{selected.company || "—"}</div>
                </div>
                <button
                  onClick={() => remove(selected.id)}
                  className="inline-flex items-center gap-2 border border-white/15 hover:border-red-500 hover:text-red-400 text-white/70 rounded-full px-4 py-2 text-xs"
                  data-testid="admin-delete"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                <div><div className="overline mb-1">Email</div><a href={`mailto:${selected.email}`} className="text-white hover:text-[#ff5865] inline-flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {selected.email}</a></div>
                <div><div className="overline mb-1">Phone</div><div className="text-white">{selected.phone || "—"}</div></div>
                <div><div className="overline mb-1">Service</div><div className="text-white">{selected.service || "—"}</div></div>
                <div><div className="overline mb-1">Budget</div><div className="text-white">{selected.budget || "—"}</div></div>
              </div>
              <div className="mt-6">
                <div className="overline mb-2">Message</div>
                <p className="text-white/80 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-white/40 text-sm">Select a submission to view details.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
 * Images
 * ========================================================================= */
function ImageSlotEditor({ slot, label, defaultUrl, currentUrl, onSaved }) {
  const [value, setValue] = useState(currentUrl || defaultUrl);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setValue(currentUrl || defaultUrl); }, [currentUrl, defaultUrl]);

  const save = async () => {
    if (!value?.trim()) return;
    setSaving(true);
    try {
      await api.put(`/images/${slot}`, { url: value.trim() });
      setSaved(true);
      onSaved?.();
      toast.success(`Saved ${label}`);
      setTimeout(() => setSaved(false), 1600);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${label}" to the built-in default?`)) return;
    setSaving(true);
    try {
      await api.delete(`/images/${slot}`);
      setValue(defaultUrl);
      onSaved?.();
      toast.success("Reset to default");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset");
    } finally { setSaving(false); }
  };

  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid={`img-slot-${slot}`}>
      <div className="flex items-center gap-2 mb-3">
        <ImageIcon className="w-3.5 h-3.5 text-[#ff5865]" />
        <div className="text-white text-sm font-medium">{label}</div>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{slot}</span>
      </div>
      <div className="aspect-video rounded-lg overflow-hidden mb-3 bg-black/40 border border-white/5">
        {value && <img src={value} alt="" className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <input
        type="url"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Paste image URL"
        className="w-full bg-black/40 border border-white/10 focus:border-[#ff5865] rounded-lg px-3 py-2 text-white text-xs font-mono placeholder-white/30 outline-none transition-colors"
        data-testid={`img-input-${slot}`}
      />
      <div className="mt-3 flex items-center gap-2">
        <button onClick={save} disabled={saving || !value?.trim()} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#ff5865] to-[#a80b1e] text-white rounded-full px-3 py-1.5 text-xs disabled:opacity-50" data-testid={`img-save-${slot}`}>
          <Save className="w-3 h-3" /> {saving ? "Saving…" : saved ? "Saved" : "Save"}
        </button>
        <button onClick={reset} className="text-white/50 hover:text-white text-xs rounded-full px-3 py-1.5 border border-white/10 hover:border-white/25 transition-colors" data-testid={`img-reset-${slot}`}>Reset default</button>
      </div>
    </div>
  );
}

function ImagesPanel() {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/images");
      setOverrides(r.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const categories = Array.from(new Set(IMAGE_SLOTS.map((s) => s.category)));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-white/72 text-sm">Update any image in-place. Paste any public URL (Unsplash, your CDN, etc.). Changes take effect on the next page load.</p>
        <button onClick={load} className="btn btn-glass !py-2 !px-4 !text-xs" data-testid="admin-images-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {categories.map((cat) => (
        <div key={cat} className="mb-10">
          <h3 className="font-display text-white text-xl mb-4">{cat}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {IMAGE_SLOTS.filter((s) => s.category === cat).map((s) => (
              <ImageSlotEditor key={s.slot} slot={s.slot} label={s.label} defaultUrl={s.default} currentUrl={overrides[s.slot]?.url} onSaved={load} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
 * Content (text / URLs)
 * ========================================================================= */
function ContentField({ slot, currentValue, onSaved }) {
  const [value, setValue] = useState(currentValue ?? slot.default);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setValue(currentValue ?? slot.default); }, [currentValue, slot.default]);

  const dirty = (value ?? "") !== (currentValue ?? slot.default);
  const isUrl = slot.type === "url";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/content/${slot.key}`, { value: value ?? "" });
      setSaved(true);
      onSaved?.();
      toast.success(`Saved ${slot.label}`);
      setTimeout(() => setSaved(false), 1400);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset "${slot.label}" to the default?`)) return;
    setSaving(true);
    try {
      await api.delete(`/content/${slot.key}`);
      setValue(slot.default);
      onSaved?.();
      toast.success("Reset to default");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to reset");
    } finally { setSaving(false); }
  };

  const Icon = isUrl ? ExternalLink : Type;

  return (
    <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]" data-testid={`content-slot-${slot.key}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5 text-[#ff5865]" />
        <div className="text-white text-sm font-medium">{slot.label}</div>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{slot.key}</span>
      </div>
      {slot.type === "textarea" ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          rows={4}
          className="w-full bg-black/40 border border-white/10 focus:border-[#ff5865] rounded-lg px-3 py-2 text-white text-sm outline-none transition-colors leading-relaxed"
          data-testid={`content-input-${slot.key}`}
        />
      ) : (
        <input
          type={slot.type === "email" ? "email" : slot.type === "phone" ? "tel" : slot.type === "url" ? "text" : "text"}
          value={value ?? ""}
          onChange={(e) => setValue(e.target.value)}
          placeholder={slot.default}
          className={`w-full bg-black/40 border border-white/10 focus:border-[#ff5865] rounded-lg px-3 py-2 text-white text-sm outline-none transition-colors ${isUrl ? "font-mono text-xs" : ""}`}
          data-testid={`content-input-${slot.key}`}
        />
      )}
      <div className="mt-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={saving || !dirty} className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#ff5865] to-[#a80b1e] text-white rounded-full px-3 py-1.5 text-xs disabled:opacity-50" data-testid={`content-save-${slot.key}`}>
            <Save className="w-3 h-3" /> {saving ? "Saving…" : saved ? "Saved" : "Save"}
          </button>
          <button onClick={reset} className="text-white/50 hover:text-white text-xs rounded-full px-3 py-1.5 border border-white/10 hover:border-white/25 transition-colors" data-testid={`content-reset-${slot.key}`}>Reset default</button>
        </div>
        {dirty && <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#ff5865]">Unsaved</span>}
      </div>
    </div>
  );
}

function ContentPanel() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/content");
      setValues(r.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const categories = Array.from(new Set(CONTENT_SLOTS.map((s) => s.category)));

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <p className="text-white/72 text-sm">Edit every headline, subtitle and button URL. Changes take effect on the next page load.</p>
        <button onClick={load} className="btn btn-glass !py-2 !px-4 !text-xs" data-testid="admin-content-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {categories.map((cat) => (
        <div key={cat} className="mb-10">
          <h3 className="font-display text-white text-xl mb-4">{cat}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {CONTENT_SLOTS.filter((s) => s.category === cat).map((s) => (
              <ContentField key={s.key} slot={s} currentValue={values[s.key]} onSaved={load} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* =========================================================================
 * Admin shell (with login gate)
 * ========================================================================= */
export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("submissions");

  if (user === null) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <div className="text-white/50 text-sm">Checking session…</div>
      </main>
    );
  }
  if (user === false) return (<><Toaster richColors theme="dark" position="top-right" /><LoginCard /></>);

  return (
    <main className="pt-28 pb-24 min-h-screen" data-testid="admin-page">
      <Toaster richColors theme="dark" position="top-right" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="tag-glass"><span className="dot" /> XMLOOPS · Admin</div>
            <h1 className="font-display text-white text-4xl md:text-5xl tracking-tight leading-tight mt-4">Site control room</h1>
            <p className="text-white/50 text-xs mt-2">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 p-1 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md">
              {[
                { id: "submissions", label: "Submissions" },
                { id: "images", label: "Images" },
                { id: "content", label: "Content" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === t.id ? "bg-white text-black" : "text-white/72 hover:text-white"}`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button onClick={logout} className="btn btn-glass !py-2 !px-4 !text-xs" data-testid="admin-logout">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-10">
          {tab === "submissions" ? <SubmissionsPanel />
            : tab === "images" ? <ImagesPanel />
            : <ContentPanel />}
        </div>
      </div>
    </main>
  );
}
